import { useCallback, useEffect, useMemo, useRef } from "react";
import { useFetcher } from "react-router-dom";

type BaseFetcher<T> = ReturnType<typeof useFetcher<T>>;
type FetcherSubmitArgs<T> = Parameters<BaseFetcher<T>["submit"]>;
export type FetcherWithSubmitPromise<T> = Omit<BaseFetcher<T>, "submit"> & {
  submit: (...args: FetcherSubmitArgs<T>) => Promise<BaseFetcher<T>>;
};
type PromiseResolveFn<T> = (result: BaseFetcher<T>) => void;

interface HookArg {
  key?: string;
  resolveTrigger?: "idle" | "loading"; // undefined = not-submitting
}

export default function usePromiseFetcher<T>({
  key,
  resolveTrigger,
}: HookArg = {}): FetcherWithSubmitPromise<T> {
  const fetcher = useFetcher<T>(key ? { key } : undefined);

  const resolveFnRef = useRef<PromiseResolveFn<T>>(() => {});

  const submit = useCallback(
    (...args: FetcherSubmitArgs<T>) => {
      return new Promise<BaseFetcher<T>>((resolve) => {
        resolveFnRef.current = resolve;
        fetcher.submit(...args);
      });
    },
    [fetcher]
  );

  useEffect(() => {
    if (
      (!resolveTrigger && fetcher.state !== "submitting") ||
      fetcher.state === resolveTrigger
    ) {
      resolveFnRef.current(fetcher);
    }
  }, [fetcher, resolveTrigger]);

  return useMemo(() => ({ ...fetcher, submit }), [fetcher, submit]);
}
